import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ChevronLeft, ChevronRight, Bookmark } from "lucide-react";
import PageHead from "@/components/PageHead";
import JobCard from "@/components/JobCard";
import CountrySelect from "@/components/CountrySelect";
import ExperienceRangeSlider from "@/components/ExperienceRangeSlider";
import DateRangeFilter, { parseJobDateTimestamp, computePresetDates } from "@/components/DateRangeFilter";
import { api } from "@/lib/api";
import { toast } from "sonner";

function JobCardSkeleton() {
  return (
    <div className="card-surface flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="skeleton-shimmer h-11 w-11 rounded-xl shrink-0" />
          <div className="space-y-2">
            <div className="skeleton-shimmer h-4 w-36 rounded-md" />
            <div className="skeleton-shimmer h-3 w-24 rounded-md" />
          </div>
        </div>
        <div className="skeleton-shimmer h-11 w-11 rounded-full shrink-0" />
      </div>

      <div className="flex items-center gap-2">
        <div className="skeleton-shimmer h-3.5 w-20 rounded-md" />
        <div className="skeleton-shimmer h-5 w-16 rounded-full" />
        <div className="skeleton-shimmer h-5 w-16 rounded-full" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <div className="skeleton-shimmer h-6 w-20 rounded-full" />
        <div className="skeleton-shimmer h-6 w-16 rounded-full" />
        <div className="skeleton-shimmer h-6 w-24 rounded-full" />
      </div>

      <div className="mt-auto flex items-center gap-2 pt-2">
        <div className="skeleton-shimmer h-9 flex-1 rounded-full" />
        <div className="skeleton-shimmer h-9 w-9 rounded-full shrink-0" />
      </div>
    </div>
  );
}

function getJobWorkModelCategory(job) {
  const m = (job.model || "").toLowerCase();
  const loc = (job.location || "").toLowerCase();
  const role = (job.role || "").toLowerCase();
  const combined = `${m} ${loc} ${role}`;

  // 1. Explicit Hybrid or Onsite/Hybrid takes priority
  if (
    m === "hybrid" ||
    m === "onsite/hybrid" ||
    m.includes("hybrid") ||
    combined.includes("hybrid") ||
    combined.includes("flexible") ||
    combined.includes("wfh days") ||
    combined.includes("remote / onsite") ||
    combined.includes("office / remote")
  ) {
    return "Hybrid";
  }

  // 2. Explicit Remote
  if (
    m.includes("remote") ||
    loc.includes("remote") ||
    loc.includes("worldwide") ||
    loc.includes("anywhere") ||
    loc.includes("global") ||
    loc === "—" ||
    combined.includes("remote") ||
    combined.includes("wfh")
  ) {
    return "Remote";
  }

  // 3. Otherwise Onsite
  return "Onsite";
}

const FILTERS = ["All", "Remote", "Hybrid", "Onsite"];

export default function FindJobs() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [filter, setFilter] = useState("All");
  const [expFilter, setExpFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(() => {
    const d = computePresetDates("last_30_days");
    return {
      preset: "last_30_days",
      start: d.start ? d.start.toISOString() : null,
      end: d.end ? d.end.toISOString() : null,
      label: "Last 30 Days",
    };
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [saved, setSaved] = useState({});
  const [savedAppsList, setSavedAppsList] = useState([]);
  const [jobList, setJobList] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleDateFilterChange = (val) => {
    setDateFilter(val);
    setPage(1);
  };

  // Fetch saved applications on mount
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const { data } = await api.get("/applications");
        const list = data?.applications || [];
        setSavedAppsList(list);
        const map = {};
        list.forEach((app) => {
          if (app.id) map[app.id] = app.id;
          const k = `${app.company}|${app.role}`.toLowerCase();
          map[k] = app.id;
        });
        setSaved(map);
      } catch (_) {}
    };
    fetchSaved();
  }, []);

  // Reset to page 1 whenever filters change
  const handleQueryChange = (val) => {
    setQ(val);
    setPage(1);
  };

  const handleCountryChange = (val) => {
    setCountry(val);
    setPage(1);
  };

  const handleFilterChange = (val) => {
    setFilter(val);
    setPage(1);
  };

  const handleExpChange = (val) => {
    setExpFilter(val);
    setPage(1);
  };

  const PAGE_SIZE = 12;

  useEffect(() => {
    let active = true;
    const fetchLiveJobs = async () => {
      if (filter === "Saved") {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const date_start = dateFilter?.start ? dateFilter.start.slice(0, 10) : undefined;
        const date_end = dateFilter?.end ? dateFilter.end.slice(0, 10) : undefined;

        const { data } = await api.get("/jobs", {
          params: {
            q,
            work_model: filter,
            country,
            experience: expFilter,
            date_start,
            date_end,
            page,
            limit: PAGE_SIZE,
          },
        });

        let fetchedJobs = data?.jobs || [];
        let fetchedTotal = data?.total || 0;
        let fetchedPages = data?.total_pages || Math.max(1, Math.ceil(fetchedTotal / PAGE_SIZE));

        if (active) {
          setJobList(fetchedJobs);
          setTotalJobs(fetchedTotal);
          setTotalPages(fetchedPages);
        }
      } catch (err) {
        if (active) {
          setJobList([]);
          setTotalJobs(0);
          setTotalPages(1);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchLiveJobs();
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [q, filter, country, expFilter, dateFilter, page]);

  const handleToggleSave = async (job) => {
    const jobKey = `${job.company}|${job.role}`.toLowerCase();
    const existingAppId = saved[job.id] || saved[jobKey];

    if (existingAppId) {
      try {
        await api.delete(`/applications/${existingAppId}`);
      } catch (_) {}
      setSaved((prev) => {
        const next = { ...prev };
        delete next[job.id];
        delete next[jobKey];
        return next;
      });
      setSavedAppsList((prev) => prev.filter((a) => a.id !== existingAppId));
      toast(`Removed ${job.role} from Application Tracker`);
    } else {
      try {
        const { data } = await api.post("/applications", {
          id: job.id,
          company: job.company,
          role: job.role,
          status: "Saved",
          location: job.location,
          match_score: job.match,
        });
        const createdApp = data?.application || { id: job.id, company: job.company, role: job.role };
        setSaved((prev) => ({ ...prev, [job.id]: createdApp.id, [jobKey]: createdApp.id }));
        setSavedAppsList((prev) => [createdApp, ...prev]);
        toast.success(`Saved ${job.role} to Application Tracker`);
      } catch (_) {
        setSaved((prev) => ({ ...prev, [job.id]: job.id, [jobKey]: job.id }));
        toast.success(`Saved ${job.role} to Application Tracker`);
      }
    }
  };

  const handlePageChange = (newPage) => {
    const maxP = filter === "Saved" ? Math.max(1, Math.ceil(savedAppsList.length / PAGE_SIZE)) : totalPages;
    if (newPage >= 1 && newPage <= maxP && newPage !== page) {
      setPage(newPage);
      window.scrollTo({ top: 120, behavior: "smooth" });
    }
  };

  const displayedJobs = useMemo(() => {
    if (filter === "Saved") {
      let list = savedAppsList;
      if (q.trim()) {
        const query = q.toLowerCase();
        list = list.filter((a) => (a.role || "").toLowerCase().includes(query) || (a.company || "").toLowerCase().includes(query));
      }
      const start = (page - 1) * PAGE_SIZE;
      return list.slice(start, start + PAGE_SIZE).map((a) => ({
        id: a.id,
        role: a.role,
        company: a.company,
        location: a.location || "Remote",
        model: "Saved",
        experience: "3-5 yrs",
        match: a.match_score || 90,
        skills: ["Saved to Tracker"],
        posted: "In Tracker",
        apply_url: "#",
      }));
    }

    return jobList;
  }, [filter, savedAppsList, jobList, q, page]);

  const activeTotal = filter === "Saved" ? savedAppsList.length : totalJobs;
  const activeTotalPages = filter === "Saved" ? Math.max(1, Math.ceil(savedAppsList.length / PAGE_SIZE)) : totalPages;
  const startIdx = activeTotal === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, activeTotal);

  return (
    <div>
      <PageHead
        label="Find Jobs"
        title="Global matches, one place"
        sub="Onsite, remote and hybrid roles aggregated and matched to your Profile."
      />

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        {/* Search input */}
        <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 shadow-xs">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            data-testid="job-search"
            value={q}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder="Search role, company or keywords…"
            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-electric shrink-0" />}
        </div>

        {/* Date Range Filter, Country Selector & Saved Jobs */}
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter value={dateFilter} onChange={handleDateFilterChange} />
          <CountrySelect value={country} onChange={handleCountryChange} />
          <button
            type="button"
            onClick={() => handleFilterChange(filter === "Saved" ? "All" : "Saved")}
            data-testid="filter-saved-jobs"
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs sm:text-sm font-semibold transition-all shadow-xs ${
              filter === "Saved"
                ? "border-electric bg-electric text-white shadow-xs"
                : "border-border bg-card text-foreground hover:bg-secondary"
            }`}
          >
            <Bookmark className={`h-4 w-4 ${filter === "Saved" ? "fill-white text-white" : "text-muted-foreground"}`} />
            <span>Saved Jobs</span>
            {savedAppsList.length > 0 && (
              <span
                className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  filter === "Saved" ? "bg-white/20 text-white" : "bg-secondary text-muted-foreground"
                }`}
              >
                {savedAppsList.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Work Model Filters */}
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <span className="text-xs font-bold text-muted-foreground mr-1 uppercase tracking-wider hidden sm:inline">Model:</span>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => handleFilterChange(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                  filter === f ? "border-electric bg-electric text-white shadow-xs" : "border-border hover:bg-secondary"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Job Count Indicator */}
          {activeTotal > 0 && (
            <p className="text-xs text-muted-foreground font-medium">
              Showing <strong className="text-foreground">{startIdx} - {endIdx}</strong> of <strong className="text-foreground">{activeTotal}</strong> jobs
            </p>
          )}
        </div>

        {/* Experience Range Draggable Slider */}
        <div className="w-full max-w-xl">
          <ExperienceRangeSlider value={expFilter} onChange={handleExpChange} />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <JobCardSkeleton key={i} />)
        ) : displayedJobs.length > 0 ? (
          displayedJobs.map((j) => {
            const isBookmarked = !!(saved[j.id] || saved[`${j.company}|${j.role}`.toLowerCase()]);
            return (
              <JobCard
                key={j.id}
                job={j}
                saved={isBookmarked}
                onSave={() => handleToggleSave(j)}
                onApply={async () => {
                  try {
                    await api.post("/applications", {
                      company: j.company,
                      role: j.role,
                      status: "Clicked",
                      location: j.location,
                      salary: j.salary,
                      match_score: j.match,
                    });
                  } catch (_) {}

                  const targetUrl = j.apply_url && j.apply_url !== "#" ? j.apply_url : j.link || j.url;
                  if (targetUrl && targetUrl !== "#") {
                    window.open(targetUrl, "_blank", "noopener,noreferrer");
                    toast.success(`Opening job posting for ${j.role}`);
                  } else {
                    toast.success(`Applied for ${j.role} (Saved to Tracker)`);
                    navigate("/app/tracker");
                  }
                }}
              />
            );
          })
        ) : (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            <p className="font-heading text-base font-semibold">No jobs found matching your criteria</p>
            <p className="text-xs mt-1">Try broadening your search query or selecting "All Countries".</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {activeTotalPages > 1 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground">
            Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{activeTotalPages}</strong>
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || loading}
              className="flex items-center gap-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: activeTotalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === activeTotalPages || Math.abs(p - page) <= 1)
                .map((p, idx, arr) => {
                  const prevP = arr[idx - 1];
                  const showEllipsis = prevP && p - prevP > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span className="px-1 text-xs text-muted-foreground">…</span>}
                      <button
                        onClick={() => handlePageChange(p)}
                        className={`h-8 w-8 rounded-lg text-xs font-bold transition-colors ${
                          p === page
                            ? "bg-electric text-white shadow-xs"
                            : "border border-border bg-card hover:bg-secondary text-foreground"
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= activeTotalPages || loading}
              className="flex items-center gap-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
